import { useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import type { RowClickedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { IReportedPost } from '~/types/report'

import columns from './columns'

import ContentLayout from '~/components/ContentLayout'
import Header from '~/components/Header/Classic'
import Layout from '~/components/Layouts/Layout'
import Section from '~/components/Section'
import TableWrapper from '~/components/Tables/TableWrapper'
import UserCard from '~/components/UserCard'
import ViewTitle from '~/components/ViewTitle'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useAppSummary } from '~/providers/AppSummary'
import { useSearchPosts } from '~/queries/reports'
import { transformByDeviceType } from '~/utils/application'

import './styles.scss'

const ProfilePosts = (): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const { profileId } = useParams()

  const { appDetails, appKey, deviceType, isLoading: isAppSummaryLoading } = useAppSummary()

  const gridRef = useRef<AgGridReact>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  /** Queries & Mutations */

  const { data: posts, isLoading: postsLoading } = useSearchPosts({
    appKey,
    deviceType,
    profileId,
  })

  /** Effects */

  useDocumentTitle('Profiles - Posts')

  /** Handlers */

  const handleRowClick = (e: RowClickedEvent<IReportedPost>) => {
    if (e.data) {
      const path = `/profiles/${profileId}/posts/${e.data.post_id}/comments`
      // @ts-expect-error 'metaKey' does not exist on type 'Event'
      if (e.event?.metaKey) {
        window.open(path, '_blank')
      } else {
        navigate(path)
      }
    }
  }

  /** Render */

  const { profile_picture_id, username } = posts?.length ? posts[0] : { profile_picture_id: '', username: '' }

  const appSelectorApp = appDetails ? transformByDeviceType(appDetails, deviceType) : undefined

  const isSwitchDisabled = !appDetails?.android || !appDetails?.ios

  return (
    <>
      <Header apps={appSelectorApp ? [appSelectorApp] : []} isSwitchDisabled={isSwitchDisabled} />
      <Layout className="posts-view" withPadding>
        <ViewTitle className="layout-view-title">
          <div>Profile Posts</div>
          {posts?.length ? <UserCard name={username} picture={profile_picture_id} /> : null}
        </ViewTitle>
        <ContentLayout loading={isAppSummaryLoading}>
          <Section>
            <TableWrapper
              className="posts-table with-min-height"
              columnDefs={columns}
              entriesName="profile-posts"
              onRowClicked={handleRowClick}
              ref={gridRef}
              rowData={postsLoading ? undefined : posts}
              tableRef={tableRef}
              withSearchBar
            />
          </Section>
        </ContentLayout>
      </Layout>
    </>
  )
}
export default ProfilePosts
